import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { Calendar } from "src/model/class/general classes/calendar";
import { ListSelectable } from "src/model/class/general classes/list-selectable";
import { InnerComponentDataError } from "src/model/enum/enumInnerComponentDataError";
import { AlertService } from "src/service/alert.service";
import { CalendarService } from "src/service/calendar.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { Settings } from "src/model/class/settings";

@Component({
    selector: "app-list-calendars-selectable",
    templateUrl: "./list-calendars.component.html",
    styleUrls: ["./list-calendars.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 *
 *
 *  Generic list calendar selectable.
 *  NB: In onClickCalendar event i don't store current notification selected beacuse it's not important at this point.
 *  I use OnAssignDisassignNotificationToGenericObjectEvent to propagate item list selection/unselection
 *
 *
 */
export class ListCalendarsSelectableComponent implements OnInit, OnDestroy {
  private _logContext: string = "List-calendar.component";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsUserSub: Subscription = null;

  //#endregion

  private _calendarSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  private _isForcingReloading: boolean = false;

  //#region  Translate string
  public lblSearchCalendar: string = "";
  private _lblNoCalendarFound: string = "";
  //#endregion

  public calendars: Array<Calendar> = null;
  public searchText = "";
  public noContentText = "Nessun calendario trovato";
  public showErrorContent = false;
  public allowEdit = true;

  @Input("IdToSelect") idToSelect: Array<number>;
  //Indicates whether calendars are for weekly planning or for schedules
  @Input("selectedComp") selectedComp: string;

  /**
   * If component is being displaying again after a previous initilisation resulted in a data-fetching error, this flag is TRUE;
   * this will result in triggering of the associated service "forceReload" method. Otherwise, it is set to FALSE.
   */
  @Input("isForcingReloading") isForcingReloading: boolean;

  /**
   * This emttter is used to signal the "father" component/page that a general error has been launched inside the list
   * (USUALLY, this means an error occurred in fetching the displayed data, whhich means an error occurred in the APIs calls
   * used to populate the store).
   * NOTE: This solution is NECESSARY because the "Error-content.component" can NOT be used directly inside this component,
   * due to circular dependency.
   */
  @Output() errorInCalendarsList = new EventEmitter<InnerComponentDataError>();

  public toolBarColor: string = "primary";

  private _networkServiceSubscription: Subscription = null;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  constructor (
    private _calendarService: CalendarService,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _loggerService: LoggerService,
    private _store: Store
  ) { }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  async ngOnInit() {
    this._isForcingReloading = this.isForcingReloading;

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();

    this._calendarService.Init();

    await this.subscribeCalendarService();

    if (this._isForcingReloading) {
      this.forceReloadData();
    }

    /** GF: enable edit only if the user can use it */
    this._selectServerSettingsUserSub = this._store
      .select(serverSettings)
      .pipe(filter((setting) => setting !== null))
      .subscribe((settingsValue) => {
        if (this.selectedComp === "schedules") {
          if (!settingsValue.ShowSchedule_Editing) {
            this.allowEdit = false;
          }
        } else if (this.selectedComp === "weeklyplanning") {
          /* Assigning a calendar to a planning is part of its registry data. */
          if (!Settings.isEnabled(settingsValue.AllowWeeklyPlanningEditOption)) {
            this.allowEdit = false;
          }
        }
      });
  }

  ngOnChanges() {
    this.idToSelect = this.idToSelect ??= [];
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this._calendarSubscription.unsubscribe();
    this._translateSubscription.unsubscribe();
    this._networkServiceSubscription.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.searchCalendar", "dictionary.noCalendarFound"])
      .subscribe((translation) => {
        this.lblSearchCalendar = translation["dictionary.searchCalendar"];
        this._lblNoCalendarFound = translation["dictionary.noCalendarFound"];

        this.noContentText = this._lblNoCalendarFound;
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   *
   * @param idCalendar: id calendar to check selection
   *
   * {@link setCalendarChecked} check if calendar id is need to check on initialization of entire component list
   *
   */
  public setCalendarChecked(idCalendar: number): boolean {
    return this.idToSelect.includes(idCalendar);
  }

  /**
   *
   * @param idCalendar: id of clicked calendar
   *
   * @param calendarElement : refrence of specific IonToggle
   *
   * {@link onClickCalendar} is fired when ion toggle is clicked: when it happens,
   * it propagate OnAssignDisassignCalendarToGenericObjectEvent event passing current calendar id and it's selection
   *
   */
  public onClickCalendar(idCalendar: number, calendarElement: any): void {
    const itemCalendar: ListSelectable = new ListSelectable();

    itemCalendar.id = idCalendar;
    itemCalendar.checked = !calendarElement.checked;

    this._eventService.OnAssignDisassignCalendarToGenericObjectEvent.next(
      itemCalendar
    );
  }

  public onSearch(searchElement: any): void {
    this.searchText = searchElement.value;
  }

  /**
   * Force reload data after "Something went wrong" button refresh click
   */
  public forceReloadData(): void {
    this._calendarService.reloadServiceData();
    this._isForcingReloading = false;
  }

  private async handleApiResponseError(): Promise<void> {
    this.showErrorContent = true;

    this.errorInCalendarsList.emit(InnerComponentDataError.LIST_CALENDARS);
  }

  private async subscribeCalendarService(): Promise<void> {
    this._alertService.clearAlertOk();

    this._calendarSubscription = this._calendarService.calendarList
      .pipe(
        filter((calendarList) => !this._isForcingReloading),
        filter((calendarList) => calendarList !== null)
      )
      .subscribe((calendarList: Array<Calendar>) => {
        //Here we handle error in API call
        if (this._calendarService.isErrorInGetcalendarListAPIRequest) {
          this.handleApiResponseError();
        } else {
          this.calendars = calendarList;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_calendarService",
            "calendarList",
            "Calendarlist: "
          );
          for (const entry of calendarList) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_calendarService",
              "calendarList",
              "Calendarlist - ENTRY: " + entry[0]
            );
          }

          this.showErrorContent = false;
        }
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectServerSettingsUserSub) {
      this._selectServerSettingsUserSub.unsubscribe();
    }
  }
  //#endregion
}
